// src/translations/privacyPolicy.js

const t = {
  en: {
    pageTitle: 'Privacy Policy (GDPR Compliance)',
    lastUpdated: 'Last updated: 19 October 2025',

    s1Title: '1. Who We Are',
    s1Controller: 'Data Controller:',
    s1AddressToggle: 'Click to see address',
    s1ContactEmail: 'Contact Email:',
    s1DPO: 'Data Protection Officer:',
    s1Body:
      'We determine the purposes and means of processing your personal data when you use our services.',

    s2Title: '2. What Data We Collect',
    s2Intro: 'When you use our platform, we may collect the following categories of data:',
    s21Title: '2.1. Information You Provide',
    s21Items: [
      'Registration Data: Name, email address, password, and other details required to create an account.',
      'Profile Information: Photos, descriptions, interests, skills, and other information you choose to share.',
      'Communication Data: Messages, connection requests, and interactions with the AI or other users.',
      'Preferences and Settings: Choices regarding connection types, notifications, and privacy settings.',
    ],
    s22Title: '2.2. Automatically Collected Data',
    s22Items: [
      'Usage Data: Pages visited, time spent, clicks, and other interactions with the platform.',
      'Technical Data: IP address, browser type, operating system, and device information.',
      'Cookies and Similar Technologies: To enhance user experience and analyze usage (see Cookies section).',
    ],

    s3Title: '3. How We Use Your Data',
    s3Items: [
      'Provide and improve our AI-based matching service.',
      'Personalise your experience and recommendations.',
      'Ensure platform security and prevent misuse.',
      'Comply with our legal obligations.',
    ],
    s3Footer: 'We will not use your data for purposes incompatible with those described above.',

    s4Title: '4. Legal Basis for Processing',
    s4Intro: 'We process your personal data under one or more of the following lawful bases:',
    s4Items: [
      'Consent (Article 6(1)(a) GDPR): You provide consent for processing your personal data, especially for AI-driven matching and optional profiling.',
      'Contractual necessity (Article 6(1)(b)): To perform our obligations under the Terms of Service.',
      'Legitimate interests (Article 6(1)(f)): For improving services, ensuring security, and preventing misuse.',
      'Legal obligation (Article 6(1)(c)): To comply with applicable laws and regulations.',
    ],
    s4Footer: 'You can withdraw your consent at any time by contacting us.',

    s5Title: '5. How We Share Your Data',
    s5Intro: 'We may share your data with:',
    s5Items: [
      'Other Users: Information you choose to make public on your profile or through messages.',
      'Service Providers: Trusted partners that help us operate our platform (e.g., hosting, analytics, AI model providers).',
      'Legal Authorities: When required by law or in response to lawful requests.',
    ],
    s5Footer: 'We never sell your personal data to third parties.',

    s6Title: '6. Data Retention',
    s6Body:
      'We retain your personal data only as long as necessary for the purposes described in this policy or as required by law. You can request deletion of your account and associated data at any time by contacting us.',

    s7Title: '7. International Data Transfers',
    s7Body:
      'If we transfer data outside the European Economic Area (EEA), we ensure appropriate safeguards are in place, such as Standard Contractual Clauses (SCCs) or other legal mechanisms approved by the European Commission.',

    s8Title: '8. Your Data Protection Rights',
    s8Intro: 'Under the GDPR, you have the following rights:',
    s8Items: [
      'Right to access – Request a copy of your personal data.',
      'Right to rectification – Request correction of inaccurate or incomplete data.',
      'Right to erasure ("right to be forgotten") – Request deletion of your personal data.',
      'Right to restriction of processing – Request limitation of how we process your data.',
      'Right to data portability – Request to receive your data in a structured, commonly used format.',
      'Right to object – Object to data processing based on legitimate interests.',
    ],
  },

  fr: {
    pageTitle: 'Politique de Confidentialité (Conformité RGPD)',
    lastUpdated: 'Dernière mise à jour : 19 octobre 2025',

    s1Title: '1. Qui nous sommes',
    s1Controller: 'Responsable du traitement des données :',
    s1AddressToggle: "Cliquez pour voir l'adresse",
    s1ContactEmail: 'Contact Email :',
    s1DPO: 'Délégué à la protection des données :',
    s1Body:
      'Nous déterminons les finalités et les moyens du traitement de vos données personnelles lorsque vous utilisez nos services.',

    s2Title: '2. Quelles données nous collectons',
    s2Intro:
      'Lorsque vous utilisez notre plateforme, nous pouvons collecter les catégories de données suivantes :',
    s21Title: '2.1. Informations que vous fournissez',
    s21Items: [
      "Données d'inscription : Nom, adresse e-mail, mot de passe, et autres détails nécessaires pour créer un compte.",
      'Informations de profil : Photos, descriptions, intérêts, compétences, et autres informations que vous choisissez de partager.',
      "Données de communication : Messages, requêtes de mise en relation, et interactions avec l'IA ou d'autres utilisateurs.",
      'Préférences et paramètres : Choix concernant les types de connexions, notifications, et paramètres de confidentialité.',
    ],
    s22Title: '2.2. Données collectées automatiquement',
    s22Items: [
      "Données d'utilisation : Pages visitées, temps passé, clics, et autres interactions avec la plateforme.",
      "Données techniques : Adresse IP, type de navigateur, système d'exploitation, et informations sur l'appareil.",
      "Cookies et technologies similaires : Pour améliorer l'expérience utilisateur et analyser l'utilisation (voir section Cookies).",
    ],

    s3Title: '3. Comment nous utilisons vos données',
    s3Items: [
      "Fournir et améliorer notre service de mise en relation basé sur l'IA.",
      'Personnaliser votre expérience et vos recommandations.',
      'Assurer la sécurité de la plateforme et prévenir les abus.',
      'Respecter nos obligations légales.',
    ],
    s3Footer: "Nous n'utiliserons pas vos données à des fins incompatibles avec celles décrites ci-dessus.",

    s4Title: '4. Base légale pour le traitement',
    s4Intro: 'Nous traitons vos données personnelles sur une ou plusieurs des bases légales suivantes :',
    s4Items: [
      "Consentement (Article 6(1)(a) RGPD) : Vous donnez votre consentement pour le traitement de vos données personnelles, en particulier pour la mise en relation basée sur l'IA et le profilage facultatif.",
      'Nécessité contractuelle (Article 6(1)(b)) : Pour exécuter nos obligations en vertu des Conditions de Service.',
      "Intérêts légitimes (Article 6(1)(f)) : Pour améliorer les services, assurer la sécurité et prévenir les abus.",
      'Obligation légale (Article 6(1)(c)) : Pour se conformer aux lois et réglementations applicables.',
    ],
    s4Footer: 'Vous pouvez retirer votre consentement à tout moment en nous contactant.',

    s5Title: '5. Comment nous partageons vos données',
    s5Intro: 'Nous pouvons partager vos données avec :',
    s5Items: [
      'Autres utilisateurs : Les informations que vous choisissez de rendre publiques sur votre profil ou via des messages.',
      "Fournisseurs de services : Partenaires de confiance qui nous aident à exploiter notre plateforme (par exemple, hébergement, analyses, fournisseurs de modèles IA).",
      'Autorités légales : Lorsque requis par la loi ou en réponse à des demandes légales.',
    ],
    s5Footer: 'Nous ne vendons jamais vos données personnelles à des tiers.',

    s6Title: '6. Conservation des données',
    s6Body:
      'Nous conservons vos données personnelles uniquement aussi longtemps que nécessaire pour les finalités décrites dans cette politique ou tel que requis par la loi. Vous pouvez demander la suppression de votre compte et des données associées à tout moment en nous contactant.',

    s7Title: '7. Transferts internationaux de données',
    s7Body:
      "Si nous transférons des données en dehors de l'Espace Économique Européen (EEE), nous nous assurons que des garanties appropriées sont en place, telles que les Clauses Contractuelles Types (CCT) ou autres mécanismes légaux approuvés par la Commission Européenne.",

    s8Title: '8. Vos droits en matière de protection des données',
    s8Intro: 'En vertu du RGPD, vous disposez des droits suivants :',
    s8Items: [
      "Droit d'accès – Demander une copie de vos données personnelles.",
      'Droit de rectification – Demander la correction de données inexactes ou incomplètes.',
      "Droit à l'effacement (« droit à l'oubli ») – Demander la suppression de vos données personnelles.",
      'Droit à la limitation du traitement – Demander une limitation de la manière dont nous traitons vos données.',
      'Droit à la portabilité des données – Demander à recevoir vos données dans un format structuré et couramment utilisé.',
      "Droit d'opposition – S'opposer au traitement des données basé sur des intérêts légitimes.",
    ],
  },
};

export const getPrivacyPolicyText = (lang) => t[lang] ?? t.en;
